import React, {useCallback, useEffect, useState} from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import {
  cancelDependentAppointment,
  fetchDependentCancellationPolicy,
  fetchDependentRescheduleOptions,
  messageFor,
  rescheduleDependentAppointment,
} from "../../api/dependents";
import {APP_LOCALE, formatDateTime} from "./format";

// Cancelling and rescheduling a dependent's appointment.
//
// Both dialogs talk to the appointment's OWN canonical endpoints — the same
// ones the guardian's personal appointments use — because the appointment names
// whose it is and the server derives the subject from the record. There is no
// dependent-scoped cancel URL to keep in step, no second cancellation window,
// and no second fee rule.
//
// The clinic's policy is asked BEFORE anything is offered, and its refusal is
// shown in the clinic's own words. A portal that decides for itself when a
// cancellation is too late will eventually disagree with the clinic that set
// the number.
//
// No `window.confirm` anywhere: a native dialog cannot say what the clinic's
// notice period is, cannot be read by the same assistive tech as the page, and
// blocks the tab it opens on.

const DAY_MS = 24 * 60 * 60 * 1000;
const HORIZON_DAYS = 28;

/** The clinic's answer to "can this be changed online at all?", loaded once. */
function useModificationPolicy(state, appointmentId, isOpen) {
  const [policy, setPolicy] = useState(null);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !appointmentId) return;
    let cancelled = false;
    setStatus("loading");
    fetchDependentCancellationPolicy(state, appointmentId)
      .then((data) => {
        if (cancelled) return;
        setPolicy(data);
        setStatus("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        setError(messageFor(err, "We could not check your clinic's policy."));
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [state, appointmentId, isOpen]);

  return {policy, status, error};
}

function PolicyBlock({policy, error}) {
  const message = error || policy?.blockedMessage;
  if (!message) return null;
  return (
    <Alert status="warning" borderRadius="10px" fontSize="sm" alignItems="flex-start">
      <AlertIcon />
      <Box>
        <Text>{message}</Text>
        {policy?.clinicPhone && (
          <Text mt={1} fontWeight="600">
            {policy.clinicPhone}
          </Text>
        )}
      </Box>
    </Alert>
  );
}

export function CancelAppointmentDialog({
  state,
  appointment,
  displayName,
  timeZone,
  isOpen,
  onClose,
  onDone,
}) {
  const {policy, status, error} = useModificationPolicy(state, appointment?.appointmentId, isOpen);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  const canProceed = status === "ready" && policy?.canModify === true;

  const submit = async () => {
    setSubmitting(true);
    try {
      await cancelDependentAppointment(state, appointment.appointmentId, reason.trim() || undefined);
      toast({
        status: "success",
        title: "Appointment cancelled",
        description: `${displayName}'s appointment has been cancelled and the clinic has been told.`,
        duration: 6000,
        isClosable: true,
      });
      onDone?.();
      onClose();
    } catch (err) {
      toast({
        status: "error",
        title: "It could not be cancelled",
        description: messageFor(err, "Please try again, or contact your clinic."),
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color="#1a1f36">Cancel this appointment</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <Box border="1px solid" borderColor="gray.200" borderRadius="10px" p={4}>
              <Text fontWeight="600" color="#1a1f36">
                {appointment?.serviceName || "Appointment"}
              </Text>
              <Text fontSize="sm" color="gray.600">
                {formatDateTime(appointment?.startsAt, timeZone)}
                {appointment?.clinicianName ? ` · ${appointment.clinicianName}` : ""}
              </Text>
              <Text fontSize="sm" color="gray.600" mt={1}>
                For {displayName}
              </Text>
            </Box>

            {status === "loading" && (
              <Flex align="center" gap={2}>
                <Spinner size="sm" color="orange.400" />
                <Text fontSize="sm" color="gray.600">
                  Checking your clinic's cancellation policy…
                </Text>
              </Flex>
            )}

            <PolicyBlock policy={policy} error={error} />

            {canProceed && (
              <Box>
                <Text fontSize="sm" color="gray.700" mb={2}>
                  Would you like to tell the clinic why? (optional)
                </Text>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="No longer needed, unwell, schedule clash…"
                  rows={3}
                  fontSize="sm"
                />
              </Box>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose} isDisabled={submitting}>
            Keep it
          </Button>
          <Button
            colorScheme="red"
            onClick={submit}
            isLoading={submitting}
            isDisabled={!canProceed}
          >
            Cancel appointment
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export function RescheduleAppointmentDialog({
  state,
  appointment,
  displayName,
  timeZone: clinicTimeZone,
  isOpen,
  onClose,
  onDone,
}) {
  const {policy, status: policyStatus, error: policyError} = useModificationPolicy(
    state,
    appointment?.appointmentId,
    isOpen
  );
  const [days, setDays] = useState(null);
  // Starts as the zone the list was drawn in, and is replaced by the one the
  // availability answer names. Both are the clinic's; they can differ only if
  // the appointment sits at a site with its own zone, and then the second is
  // the right one — jurisdiction and clock both follow the site.
  const [timeZone, setTimeZone] = useState(clinicTimeZone || null);
  const [optionsStatus, setOptionsStatus] = useState("idle");
  const [optionsError, setOptionsError] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  const canProceed = policyStatus === "ready" && policy?.canModify === true;

  const loadOptions = useCallback(async () => {
    setOptionsStatus("loading");
    setOptionsError(null);
    try {
      const now = Date.now();
      // The appointment to leave out of the search is the one being moved, and
      // the server derives it from the path — an appointment must not block its
      // own reschedule, and a client naming the exclusion is a client the server
      // would have to trust about whose appointment it is.
      const data = await fetchDependentRescheduleOptions(state, appointment.appointmentId, {
        from: now,
        to: now + HORIZON_DAYS * DAY_MS,
      });
      setDays(data?.days || {});
      // The clinic's own time zone, from the answer. Every time on this screen
      // is drawn in it — see the note on `timeLabel`.
      setTimeZone(data?.meta?.timeZone || null);
      setOptionsStatus("ready");
    } catch (err) {
      setOptionsError(messageFor(err, "We could not load available times."));
      setOptionsStatus("error");
    }
  }, [state, appointment?.appointmentId]);

  useEffect(() => {
    if (canProceed && optionsStatus === "idle") loadOptions();
  }, [canProceed, optionsStatus, loadOptions]);

  useEffect(() => {
    if (!isOpen) {
      setOptionsStatus("idle");
      setChosen(null);
      setDays(null);
      setTimeZone(clinicTimeZone || null);
    }
  }, [isOpen, clinicTimeZone]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await rescheduleDependentAppointment(state, appointment.appointmentId, chosen);
      toast({
        status: "success",
        title: "Appointment moved",
        description: `${displayName}'s appointment is now ${dayLabel(chosen.dayKey)} at ${timeLabel(chosen.start, timeZone)}.`,
        duration: 6000,
        isClosable: true,
      });
      onDone?.();
      onClose();
    } catch (err) {
      toast({
        status: "error",
        title: "It could not be moved",
        description: messageFor(err, "That time may have just been taken. Please pick another."),
        duration: 8000,
        isClosable: true,
      });
      // The slot list is now stale by definition — somebody else took it.
      loadOptions();
      setChosen(null);
    } finally {
      setSubmitting(false);
    }
  };

  const dayEntries = Object.entries(days || {})
    .filter(([, day]) => (day?.slots || []).length > 0)
    .sort(([a], [b]) => (a < b ? -1 : 1));

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="85vh">
        <ModalHeader color="#1a1f36">Move this appointment</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <Box border="1px solid" borderColor="gray.200" borderRadius="10px" p={4}>
              <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.04em" color="gray.500">
                Currently
              </Text>
              <Text fontWeight="600" color="#1a1f36">
                {formatDateTime(appointment?.startsAt, timeZone)}
              </Text>
              <Text fontSize="sm" color="gray.600">
                {appointment?.serviceName || "Appointment"}
                {appointment?.clinicianName ? ` · ${appointment.clinicianName}` : ""} · for{" "}
                {displayName}
              </Text>
            </Box>

            {policyStatus === "loading" && (
              <Flex align="center" gap={2}>
                <Spinner size="sm" color="orange.400" />
                <Text fontSize="sm" color="gray.600">
                  Checking your clinic's policy…
                </Text>
              </Flex>
            )}

            <PolicyBlock policy={policy} error={policyError} />

            {canProceed && optionsStatus === "loading" && (
              <Flex align="center" gap={2} py={4}>
                <Spinner size="sm" color="orange.400" />
                <Text fontSize="sm" color="gray.600">
                  Finding available times…
                </Text>
              </Flex>
            )}

            {canProceed && optionsStatus === "error" && (
              <Alert status="error" borderRadius="10px" fontSize="sm">
                <AlertIcon />
                {optionsError}
              </Alert>
            )}

            {canProceed && optionsStatus === "ready" && dayEntries.length === 0 && (
              <Alert status="info" borderRadius="10px" fontSize="sm">
                <AlertIcon />
                There are no free times in the next {HORIZON_DAYS} days. Please contact the clinic.
              </Alert>
            )}

            {canProceed &&
              optionsStatus === "ready" &&
              dayEntries.map(([isoDay, day]) => (
                <Box key={isoDay}>
                  <Text
                    fontSize="11px"
                    letterSpacing="0.04em"
                    textTransform="uppercase"
                    color="gray.500"
                    mb={2}
                  >
                    {dayLabel(isoDay)}
                  </Text>
                  <Flex wrap="wrap" gap={2}>
                    {day.slots.map((slot) => {
                      const selected = chosen?.start === slot.start;
                      return (
                        <Button
                          key={`${slot.start}-${slot.did || ""}`}
                          size="sm"
                          variant={selected ? "solid" : "outline"}
                          colorScheme={selected ? "orange" : "gray"}
                          onClick={() => setChosen({...slot, dayKey: isoDay})}
                          sx={{fontVariantNumeric: "tabular-nums"}}
                        >
                          {timeLabel(slot.start, timeZone)}
                        </Button>
                      );
                    })}
                  </Flex>
                </Box>
              ))}
          </Stack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose} isDisabled={submitting}>
            Leave it as it is
          </Button>
          <Button
            colorScheme="orange"
            onClick={submit}
            isLoading={submitting}
            isDisabled={!chosen}
          >
            {chosen ? `Move to ${timeLabel(chosen.start, timeZone)}` : "Pick a time"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Times are the CLINIC's, not the browser's
// ---------------------------------------------------------------------------
//
// Every time on this screen is drawn in the location's own time zone, which the
// availability answer carries in `meta.timeZone`. A guardian travelling, or one
// who simply lives in another province from the clinic, would otherwise be
// offered "9:00" under Tuesday's heading and arrive at 11:00 on the clinic's
// clock — the day comes from the server and the time from the browser, and the
// two disagree by exactly the offset.
//
// The day heading is built from the KEY STRING for the same reason and one
// more: `new Date("2026-09-30")` is parsed as UTC midnight and lands on the
// 29th for everybody west of Greenwich.
function dayLabel(isoDay) {
  const [year, month, day] = String(isoDay || "").split("-").map(Number);
  if (!year || !month || !day) return isoDay || "";
  return new Date(year, month - 1, day).toLocaleDateString(APP_LOCALE, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

function timeLabel(ms, timeZone) {
  if (!ms) return "";
  return new Date(Number(ms)).toLocaleTimeString(APP_LOCALE, {
    hour: "numeric",
    minute: "2-digit",
    ...(timeZone ? {timeZone} : {}),
  });
}
